import { Form, Input, Select, Typography } from "antd";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { RuleConditionNumberOp } from "../../../api/graphql/schema.generated";
import { Direction } from "../../../types";
import {
  encodeFormAmountCondition,
  getAmountFromString,
  getLocaleAmountString,
} from "./helpers";
import { currencyFormatter } from "../../../utils";

export type AmountInputProps = {
  name: string[];
  direction?: Direction;
  amount: AmountInputValue;
};

export type AmountInputValue = {
  op: RuleConditionNumberOp;
  value?: string;
};

export const AmountInput = (props: AmountInputProps) => {
  const { t } = useTranslation("Rules");
  const opInputName = [...props.name, "op"];

  return (
    <Form.Item
      name={[...props.name, "value"]}
      label={t("amount.label")}
      extra={displayExtraMessage(props, t)}
      getValueProps={(value = "") => ({
        value: getLocaleAmountString(value),
      })}
      getValueFromEvent={(event: ChangeEvent<HTMLInputElement>) =>
        getAmountFromString(event.target.value)
      }
      rules={[
        {
          validator: async (_, value: string) => {
            if (props.direction) {
              const amountCondition = encodeFormAmountCondition(
                props.direction,
                {
                  op: props.amount.op,
                  value,
                }
              );
              if (amountCondition instanceof Error) {
                throw amountCondition;
              }
            }
          },
        },
      ]}
      dependencies={[opInputName]}
    >
      <Input
        addonBefore={
          <Form.Item name={opInputName} noStyle>
            <Select>
              <Select.Option value={RuleConditionNumberOp.EQ}>
                {t("amount.op.EQ")}
              </Select.Option>
              <Select.Option value={RuleConditionNumberOp.LT}>
                {t("amount.op.LT")}
              </Select.Option>
              <Select.Option value={RuleConditionNumberOp.GT}>
                {t("amount.op.GT")}
              </Select.Option>
            </Select>
          </Form.Item>
        }
        style={{ maxWidth: "280px", display: "block" }}
        placeholder={String(t("amount.placeholder"))}
        suffix={
          <Typography.Text type={props.amount?.value ? undefined : "secondary"}>
            €
          </Typography.Text>
        }
        disabled={!props.direction}
      />
    </Form.Item>
  );
};

function displayExtraMessage(
  { direction, amount }: AmountInputProps,
  t: TFunction
): string | undefined {
  if (!direction || !amount?.op || !amount?.value) {
    return;
  }

  const amountCondition = encodeFormAmountCondition(direction, amount);
  if (amountCondition instanceof Error) {
    return;
  }

  const absAmountInCents = Math.abs(amountCondition.value);
  const displayedDirection = displayDirection(direction, t);

  if (amount.op === RuleConditionNumberOp.EQ) {
    return String(
      t("amount.result.exact", {
        amount: displayAmountInCents(absAmountInCents),
        direction: displayedDirection,
      })
    );
  }

  const adjustedAmount = displayAmountInCents(
    absAmountInCents + (amount.op === RuleConditionNumberOp.LT ? -1 : 1)
  );

  if (amount.op === RuleConditionNumberOp.LT) {
    return String(
      t("amount.result.lessThan", {
        amount: adjustedAmount,
        direction: displayedDirection,
      })
    );
  }

  return String(
    t("amount.result.greaterThan", {
      amount: adjustedAmount,
      direction: displayedDirection,
    })
  );
}

function displayAmountInCents(amountInCents: number): string {
  return currencyFormatter.format(amountInCents / 100);
}

function displayDirection(direction: Direction, t: TFunction): string {
  return direction === Direction.INCOMING
    ? t("amount.direction.incoming")
    : t("amount.direction.outgoing");
}
